import { jwtDecode } from "jwt-decode";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Api, isAuthTokenBodyEndpoint } from "../services/api-config";
import { URLS } from "../services/app-urls";
import { fetchHubLoginUrl, renewSession } from "../services/hub-settings";
import { redirectToLoginHub } from "../utils/redirect-login-utils";
import { BaseService } from "../services/base-service";
import { authTokenStorage, isTokenExpired } from "../utils/auth-token-storage";
import {
  claimPendingRedirect,
  consumePendingRedirect,
  peekPendingRedirect,
} from "../utils/post-login-redirect";
import { publishAuthMessage, subscribeToAuthBroadcast } from "../utils/auth-broadcast";
import SessionExpiredDialogComponent from "../components/session-expired-dialog/session-expired-dialog.component";

type TokenMessage = { access: string; refresh: string };

// Extracted from the response interceptor below so it's testable as a plain
// function -- tests mock the Api instance directly (see tests/setup.ts),
// which never runs an axios interceptor for real.
export const handle470Response = (error: any, onSessionExpired: () => void): Promise<any> => {
  if (error?.response?.status !== 470) return Promise.reject(error);

  // register_session/local_fallback are awaited synchronously by the SSO
  // handshake and the login form -- they must reject for real instead of
  // hanging here like every other authenticated call does below.
  if (isAuthTokenBodyEndpoint(error?.config?.url)) {
    console.error("Auth bootstrap call rejected with 470:", error?.config?.url);
    return Promise.reject(error);
  }

  error.__handled = true;
  onSessionExpired();
  return new Promise(() => { });
};

// The Hub posts the token via a blind `setTimeout(..., 2000)` on its side,
// with no check that this tab finished loading -- give it a window well
// past that fixed delay before giving up (covers slower networks too).
const HUB_HANDSHAKE_TIMEOUT_MS = 8000;

// Determines if this tab was likely opened by the Hub's SSO flow and should
// wait for an incoming token via postMessage before assuming unauthenticated state.
const wasOpenedByHub = (): boolean => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("sso") || searchParams.has("code")) {
      return true;
    }
    if (!window.opener) {
      if (!document.referrer) return false;
      return new URL(document.referrer).origin !== window.location.origin;
    }
    if (document.referrer) {
      return new URL(document.referrer).origin !== window.location.origin;
    }
    return true;
  } catch {
    return true;
  }
};

type AuthContextType = {
  authenticated: boolean;
  user: any;
  loading: boolean;
  logout: () => void;
};

export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType
);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  // Pending "give up waiting for the Hub's postMessage" timer -- cleared as
  // soon as a token shows up, either via the message handshake or another
  // tab's broadcast, whichever gets here first.
  const handshakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHandshakeTimeout = () => {
    if (handshakeTimeoutRef.current) {
      clearTimeout(handshakeTimeoutRef.current);
      handshakeTimeoutRef.current = null;
    }
  };

  // Computed once per tab -- window.opener/document.referrer don't change
  // over the tab's lifetime, and useRef's initial value is only ever used
  // on the first render.
  const wasOpenedByHubRef = useRef<boolean>(wasOpenedByHub());

  useEffect(() => {
    // A 401/470/WebSocket-auth-failure shows a modal on top of whatever the
    // user was doing instead of tearing the page down -- in-progress forms
    // survive, and they recover either by logging in again in a new tab or
    // automatically via the cross-tab broadcast below.
    const handleUnauthorized = () => {
      setSessionExpired(true);
    };
    window.addEventListener("unauthorized-event", handleUnauthorized);
    window.addEventListener("websocket-auth-failed", handleUnauthorized);

    return () => {
      window.removeEventListener("unauthorized-event", handleUnauthorized);
      window.removeEventListener("websocket-auth-failed", handleUnauthorized);
    };
  }, []);

  useEffect(() => {
    const processExistingToken = async () => {
      let existingToken = authTokenStorage.get();

      // A token surviving in localStorage past its own exp is worse than no
      // token: it would render the app authenticated for one paint, then
      // fail the first request with a 401. Ask the backend to renew it
      // first (a local-login session stays renewable long after the access
      // token's exp); only if that fails discard it and fall through to
      // the "no token" branch, same as never having had one.
      if (existingToken && isTokenExpired(existingToken)) {
        const renewed = await renewSession(existingToken);
        if (renewed && !isTokenExpired(renewed)) {
          authTokenStorage.set(renewed);
          existingToken = renewed;
        } else {
          authTokenStorage.clear();
          existingToken = null;
        }
      }

      if (existingToken) {
        try {
          const decodedToken: any = jwtDecode(existingToken);
          setUser(decodedToken.user);
          setLoading(false);
        } catch (err) {
          console.error('Erro ao decodificar token existente:', err);
          authTokenStorage.clear();
          setLoading(false);
        }
        return;
      }

      // No token in this tab. If the Hub opened it, a postMessage with the
      // token may still be in flight -- hold off on "not authenticated" so
      // <Private> doesn't bounce back to the Hub before it arrives. Any
      // other tab (deep link, bookmark, reload) gets the instant behavior.
      if (wasOpenedByHubRef.current) {
        handshakeTimeoutRef.current = setTimeout(() => {
          handshakeTimeoutRef.current = null;
          // Either the Hub's postMessage never arrived (raced the page
          // load, or access_url doesn't match this origin) or it arrived
          // and was discarded (see the warnings in handleMessage below).
          console.warn(
            "[auth] Gave up waiting for the Hub's postMessage after",
            HUB_HANDSHAKE_TIMEOUT_MS, "ms; no usable token arrived."
          );
          setLoading(false);
        }, HUB_HANDSHAKE_TIMEOUT_MS);
      } else {
        setLoading(false);
      }
    };

    processExistingToken();

    return () => clearHandshakeTimeout();
  }, []);

  useEffect(() => {
    let expectedOrigin: string | null = null;

    const fetchExpectedOrigin = async () => {
      // URL_LOGIN_HUB, not URL_HUB (the Hub's API, a different origin) --
      // the postMessage handshake is sent by the Hub's frontend, which is
      // what URL_LOGIN_HUB actually points at.
      const loginUrl = await fetchHubLoginUrl();
      if (!loginUrl) {
        // No origin to check against means the guard below is skipped
        // entirely -- any window that gets a reference to this tab could
        // send it a token. Worth knowing about, not just silently
        // permissive.
        console.warn("[auth] URL_LOGIN_HUB is not configured; accepting a token message from any origin.");
        return;
      }
      try {
        expectedOrigin = new URL(loginUrl).origin;
      } catch (err) {
        console.error('Erro ao obter a origem do Access Hub:', err);
      }
    };
    fetchExpectedOrigin();

    const handleMessage = async (event: MessageEvent<TokenMessage>) => {
      if (!event.data?.access) {
        // Only worth a warning for messages that look token-shaped but
        // aren't usable -- anything else (HMR, extensions, unrelated
        // frames) would drown this out. Never logs the payload itself.
        const data: any = event.data;
        if (data && typeof data === "object" && ("access" in data || "refresh" in data)) {
          console.warn(
            "[auth] Token-shaped message without a usable access field.",
            { origin: event.origin, keys: Object.keys(data) }
          );
        }
        return;
      }
      const isOriginAllowed = (received: string, expected: string | null): boolean => {
        if (!expected) return true;
        try {
          const expUrl = new URL(expected);
          const recUrl = new URL(received);
          if (recUrl.origin === expUrl.origin) return true;
          if (recUrl.hostname === expUrl.hostname) return true;
          if (recUrl.hostname.endsWith(".fpf.br") && expUrl.hostname.endsWith(".fpf.br")) return true;
        } catch {
          return false;
        }
        return false;
      };

      if (expectedOrigin && !isOriginAllowed(event.origin, expectedOrigin)) {
        console.warn("[auth] Token message discarded: origin mismatch.", {
          received: event.origin,
          expected: expectedOrigin,
        });
        return;
      }

      try {
        await processTokenMessage(event);
      } catch (err: any) {
        console.error('Erro ao processar mensagem de token:', err);
        if (err?.response?.status === 470) {
          // register_session rejected this brand-new token (e.g. a
          // system_code mismatch) -- redirecting back to the Hub would just
          // relogin and hand back the same rejected token, looping. Surface
          // the session-expired modal instead, which offers a real way out.
          clearHandshakeTimeout();
          setLoading(false);
          setSessionExpired(true);
          return;
        }
        await triggerLogout();
      }
    };

    const processTokenMessage = async (event: MessageEvent<TokenMessage>) => {
      // A token arriving here always wins over whatever's already in
      // storage -- this is the Hub's own postMessage (origin already
      // validated above), not a guess. Deferring to a pre-existing token
      // instead let a second user's Hub login silently inherit the first
      // user's session in the same browser.
      await storeNewToken(event.data);
      const token = event.data.access;

      await authenticateUser(token);
      setSessionExpired(false);
      publishAuthMessage({ type: "token", access: token });

      if (window.location.search.includes("sso=")) {
        const cleanParams = new URLSearchParams(window.location.search);
        cleanParams.delete("sso");
        const cleanSearch = cleanParams.toString();
        const newUrl =
          window.location.pathname +
          (cleanSearch ? `?${cleanSearch}` : "") +
          window.location.hash;
        window.history.replaceState({}, "", newUrl);
      }

      navigateToPendingRedirect();
    };

    const navigateToPendingRedirect = () => {
      const pendingPath = consumePendingRedirect();
      if (!pendingPath) return;
      // Marks it handled so a tab that recovers via the cross-tab token
      // broadcast (rather than this direct Hub handshake) knows not to
      // also navigate there -- it stays on its own current page instead.
      if (!claimPendingRedirect(pendingPath)) return;
      navigate(pendingPath);
    };

    const storeNewToken = async (tokenData: TokenMessage) => {
      authTokenStorage.set(tokenData.access);

      try {
        await Api.post(`${URLS.AUTH}register_session/`, {
          access: tokenData.access,
          refresh: tokenData.refresh,
        });
      } catch (error) {
        console.error('Erro ao registrar sessão:', error);
        // Otherwise a token the backend just refused sits in localStorage,
        // and the next reload's processExistingToken (it isn't expired,
        // just foreign) adopts it and every subsequent request 470s again.
        authTokenStorage.clear();
        throw error;
      }
    };

    const authenticateUser = async (token: string) => {
      try {
        const decodedToken: any = jwtDecode(token);
        setUser(decodedToken.user);
      } catch (err) {
        console.error('Erro ao decodificar token:', err);
        throw err;
      } finally {
        clearHandshakeTimeout();
        setLoading(false);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    const interceptor = Api.interceptors.response.use(
      response => response,
      error => handle470Response(error, () => setSessionExpired(true))
    );

    return () => {
      Api.interceptors.response.eject(interceptor);
    };
  }, [user !== null]);

  // Cross-tab recovery: even with the access token in localStorage (shared),
  // a tab already showing the expired-session modal has no reason to poll
  // storage for a token that another tab just wrote -- it's told instead,
  // and reacts immediately rather than waiting on its next API call.
  useEffect(() => {
    const unsubscribe = subscribeToAuthBroadcast((message) => {
      if (message.type === "token") {
        handleTokenBroadcast(message.access);
      } else if (message.type === "logout") {
        handleLogoutBroadcast();
      }
      // 'claimed' messages don't need their own reaction here: the pending
      // redirect claim itself lives in localStorage, which every tab checks
      // via claimPendingRedirect before deciding whether to navigate.
    });

    const handleTokenBroadcast = (access: string) => {
      // No "was this tab the sender" guard needed: BroadcastChannel never
      // delivers a message back to the tab that posted it, so this only
      // ever runs for genuine receivers.
      authTokenStorage.set(access);
      try {
        const decoded: any = jwtDecode(access);
        setUser(decoded.user);
      } catch (err) {
        console.error('Erro ao decodificar token recebido de outra aba:', err);
      }
      clearHandshakeTimeout();
      setSessionExpired(false);
      setLoading(false);

      // This tab keeps its own current URL rather than following the
      // sender's deep link, but still marks that link claimed so a third
      // tab doesn't try to navigate to a now-stale pending redirect.
      const pendingPath = peekPendingRedirect();
      if (pendingPath && claimPendingRedirect(pendingPath)) {
        publishAuthMessage({ type: "claimed", path: pendingPath });
      }
    };

    const handleLogoutBroadcast = () => {
      authTokenStorage.clear();
      setUser(null);
      setSessionExpired(false);
    };

    return unsubscribe;
  }, []);

  // Proactive expiry check: catches a token that went stale while this tab
  // was in the background, instead of waiting for the user to trigger an
  // API call that then fails.
  useEffect(() => {
    let checking = false;
    const checkExpiry = async () => {
      const token = authTokenStorage.get();
      if (!token || !isTokenExpired(token) || checking) return;

      // focus and visibilitychange fire together; one probe is enough.
      checking = true;
      try {
        const renewed = await renewSession(token);
        if (renewed && !isTokenExpired(renewed)) {
          authTokenStorage.set(renewed);
        } else {
          setSessionExpired(true);
        }
      } finally {
        checking = false;
      }
    };

    window.addEventListener("focus", checkExpiry);
    document.addEventListener("visibilitychange", checkExpiry);
    return () => {
      window.removeEventListener("focus", checkExpiry);
      document.removeEventListener("visibilitychange", checkExpiry);
    };
  }, []);

  const triggerLogout = async (reason?: string, savePendingUrl = true, userInitiated = false) => {
    authTokenStorage.clear();
    window.dispatchEvent(new Event("auth-logout"));
    setUser(null);
    setSessionExpired(false);
    publishAuthMessage({ type: "logout" });

    // The Hub's own session lives in that other tab's sessionStorage, which
    // this tab (opened via window.open()) never shares -- sending it to the
    // Hub's /login would show the form even though that tab is still logged
    // in. Closing it and landing back on the Hub tab only applies to a tab
    // the Hub itself opened, and only on an explicit user logout, not an
    // involuntary session drop (which should still offer a way back).
    if (userInitiated && wasOpenedByHubRef.current && window.opener && !window.opener.closed) {
      window.close();
    }
    await redirectToLoginHub(reason || null, savePendingUrl);
  };

  const logout = async () => {
    try {
      setLoading(true);
      const authService = new BaseService<any>(URLS.AUTH);
      await authService.postFromListRoute({}, "revoke_token");
    } catch (error) {
      console.error(error);
    } finally {
      // Explicit, user-initiated logout: don't remember where they were,
      // they chose to leave -- only involuntary session loss should bounce
      // back to the same deep link after logging back in.
      await triggerLogout(undefined, false, true);
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authenticated: !!user,
        user,
        loading,
        logout,
      }}
    >
      {children}
      <SessionExpiredDialogComponent open={sessionExpired} />
    </AuthContext.Provider>
  );
};
